import { Link, useParams } from "react-router-dom";
import PageShell from "@/components/feature/PageShell";
import EmptyState from "@/components/base/EmptyState";
import StoryCard from "@/components/feature/StoryCard";
import ShareButtons from "@/components/feature/ShareButtons";
import { getStoryById, getCastsByIds, getClipsByIds, storyList, formatDuration } from "@/utils/catalog";

export default function StoryDetailPage() {
  const { id } = useParams();
  const story = getStoryById(id);

  if (!story) {
    return (
      <PageShell active="story" crumbs={[{ label: "首页", to: "/" }, { label: "剧情介绍", to: "/category/story" }, { label: "未找到" }]}>
        <EmptyState
          title="没有找到这篇剧情"
          description="它可能已被移动或删除，返回剧情列表看看其他内容吧。"
          actionText="返回剧情列表"
          onAction={() => window.REACT_APP_NAVIGATE("/category/story")}
        />
      </PageShell>
    );
  }

  const relatedCasts = getCastsByIds(story.castIds);
  const relatedClips = getClipsByIds(story.clipIds);
  const recommend = storyList.filter((item) => item.id !== story.id && item.season === story.season).slice(0, 3);

  return (
    <PageShell
      active="story"
      crumbs={[
        { label: "首页", to: "/" },
        { label: "剧情介绍", to: "/category/story" },
        { label: story.title },
      ]}
    >
      <article>
        <div className="relative w-full overflow-hidden rounded-lg aspect-[16/9] md:aspect-[21/9]">
          <img
            src={story.cover}
            alt={`${story.title}剧照`}
            title={`${story.title} 剧情介绍`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-foreground-950/30 to-transparent"></div>
          <div className="absolute bottom-0 left-0 w-full p-5 md:p-8">
            <div className="flex flex-wrap gap-2">
              {story.tags.map((tag) => (
                <span key={tag} className="rounded-full bg-background-50/15 px-3 py-1 text-xs text-background-100 backdrop-blur">
                  {tag}
                </span>
              ))}
            </div>
            <h1 className="mt-3 font-heading text-2xl font-semibold text-background-50 md:text-4xl">
              {story.title}
            </h1>
            <p className="mt-2 text-sm text-background-200">
              {story.season} · 第 {story.episode} 集 · 更新于 {story.publishedAt}
            </p>
          </div>
        </div>

        <div className="mt-6 rounded-lg border border-background-200/70 bg-background-50 p-5 md:p-7">
          <h2 className="font-heading text-lg font-semibold text-foreground-900">剧情梗概</h2>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600">{story.summary}</p>

          <h2 className="mt-7 font-heading text-lg font-semibold text-foreground-900">分集内容</h2>
          <div className="mt-3 space-y-3">
            {story.content.map((paragraph, index) => (
              <p key={index} className="text-sm leading-loose text-foreground-600">
                {paragraph}
              </p>
            ))}
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-background-200 pt-5">
            <ShareButtons title={story.title} />
            <Link
              to="/category/story"
              className="inline-flex items-center gap-1 text-sm text-foreground-500 transition hover:text-primary-600"
            >
              <i className="ri-arrow-left-line"></i>返回剧情列表
            </Link>
          </div>
        </div>

        {relatedClips.length > 0 && (
          <section className="mt-6 rounded-lg border border-background-200/70 bg-background-100 p-5 md:p-7">
            <h2 className="font-heading text-lg font-semibold text-foreground-900">相关精彩片段</h2>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              {relatedClips.map((clip) => (
                <Link
                  key={clip.id}
                  to={`/detail/clip/${clip.id}`}
                  className="group flex items-center gap-3 rounded-lg bg-background-50 p-3 transition hover:bg-primary-50"
                >
                  <div className="relative h-16 w-28 flex-shrink-0 overflow-hidden rounded-md bg-background-200">
                    <img
                      src={clip.thumbnail}
                      alt={`${clip.title}缩略图`}
                      title={`${clip.title} 精彩片段`}
                      className="h-full w-full object-cover object-top"
                    />
                    <span className="absolute inset-0 flex items-center justify-center text-background-50 opacity-0 transition group-hover:opacity-100">
                      <i className="ri-play-circle-fill text-2xl"></i>
                    </span>
                  </div>
                  <div className="min-w-0">
                    <p className="line-clamp-2 text-sm text-foreground-800 transition group-hover:text-primary-600">
                      {clip.title}
                    </p>
                    <p className="mt-1 text-xs text-foreground-400">
                      {clip.type} · {formatDuration(clip.duration)}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        <section className="mt-6 rounded-lg border border-background-200/70 bg-background-50 p-5 md:p-7">
          <h2 className="font-heading text-lg font-semibold text-foreground-900">关联演员</h2>
          <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {relatedCasts.map((cast) => (
              <Link
                key={cast.id}
                to={`/detail/cast/${cast.id}`}
                className="group flex items-center gap-3 rounded-lg bg-background-100 p-3 transition hover:bg-primary-50"
              >
                <div className="h-12 w-12 flex-shrink-0 overflow-hidden rounded-full bg-background-200">
                  <img
                    src={cast.avatar}
                    alt={`${cast.name}头像`}
                    title={`${cast.name} 演员资料`}
                    className="h-full w-full object-cover object-top"
                  />
                </div>
                <div className="min-w-0">
                  <p className="truncate text-sm text-foreground-900 transition group-hover:text-primary-600">{cast.name}</p>
                  <p className="truncate text-xs text-foreground-500">饰 {cast.role}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {recommend.length > 0 && (
          <section className="mt-6">
            <h2 className="font-heading text-lg font-semibold text-foreground-900">相关剧情推荐</h2>
            <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 md:gap-5">
              {recommend.map((item) => (
                <StoryCard key={item.id} story={item} />
              ))}
            </div>
          </section>
        )}
      </article>
    </PageShell>
  );
}